import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import {
  Button,
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY,
  Checkbox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Flex,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  Text,
  TEXT_VARIANT_LIGHT
} from 'mailup-web-components'

import { useRecoilCallback, useRecoilState, useResetRecoilState } from 'recoil'

import { useHistory } from 'react-router'

import {
  callbackAddToWhiteList,
  showModalIfNecessaryCallback
} from './recoil/callbacks'

import { modalCheckupState } from './recoil/state'
import BlockingCheck from './components/BlockingCheck/BlockingCheck'
import PotentialCheck from './components/PotentialCheck/PotentialCheck'
import ImprovementsCheck from './components/ImprovementsCheck/ImprovementsCheck'
import LinkCheck from './components/LinkCheck/LinkCheck'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

const ModalCheckup = ({
  startWhen,
  messageType,
  isBee,
  idMessage,
  idCampaign
}) => {
  const intl = useIntl()

  const history = useHistory()
  const [
    { show, silencedCheck, blockToDisplay, checkup },
    setOpen
  ] = useRecoilState(modalCheckupState)

  const callbackAddToWList = useRecoilCallback(callbackAddToWhiteList, [])
  const showModalIfNecessary = useRecoilCallback(
    showModalIfNecessaryCallback,
    []
  )
  const resetModalState = useResetRecoilState(modalCheckupState)

  useEffect(() => {
    startWhen && showModalIfNecessary(idMessage)
  }, [startWhen])

  const makeTitle = () => {
    let title

    if (checkup && checkup.blocking > 0) {
      title = intl.formatMessage({
        id: 'Load.EmailCheckUp.ModalCheckUpKoTitle'
      })
    } else if (checkup && checkup.potential > 0) {
      title = intl.formatMessage({
        id: 'Load.EmailCheckUp.ModalCheckUpWarningTitle'
      })
    } else {
      title = intl.formatMessage({
        id: 'Load.EmailCheckUp.ModalCheckUpTitle'
      })
    }

    return title
  }

  const handleIgnoreAndClose = () => {
    if (checkup && checkup.blocking === 0) {
      callbackAddToWList(idMessage)
      setOpen(prev => ({
        ...prev,
        show: false,
        silencedCheck: false
      }))
      setTimeout(() => {
        resetModalState() // reset state
      }, 500)
    }
  }

  const handleSaveAndClose = () => {
    callbackAddToWList(idMessage)
    setOpen(prev => ({
      ...prev,
      show: false,
      silencedCheck: false
    }))

    history.push(`/messages/email/${idMessage}/checkup/summary`)
  }

  const handleCheckChange = e => {
    const checked = e.target.checked
    setOpen(prev => ({
      ...prev,
      silencedCheck: checked
    }))
  }

  const handleClick = () => {
    setOpen(prev => ({
      ...prev,
      silencedCheck: !prev.silencedCheck
    }))
  }

  const handleClickDontShow = e => {
    e.preventDefault()
    e.stopPropagation()

    history.push(`/messages/email/${idMessage}/checkup/summary`)
  }

  const handleOverview = e => {
    e.preventDefault()
    e.stopPropagation()

    history.push(`/messages/email/${idMessage}/overview`)
  }

  return (
    <Modal
      show={show}
      showClose={checkup && checkup.blocking === 0}
      closeOnOverlayClick={checkup && checkup.blocking === 0}
      onClose={handleIgnoreAndClose}
      data-mu-test='modal-checkup-message__modal-checkup-field'
    >
      <ModalHead title={makeTitle()} />
      <ModalBody scrollable>
        <Flex maxHeight='400px' flexDirection='column'>
          <Container>
            {checkup && checkup.blocking > 0 && (
              <BlockingCheck idMessage={idMessage} />
            )}
            {checkup &&
              checkup.blocking === 0 &&
              checkup.potential > 0 &&
              blockToDisplay &&
              blockToDisplay.some(
                e => e.section === 'POTENTIAL' && e.toDisplay === true
              ) && (
                <PotentialCheck
                  messageType={messageType}
                  isBee={isBee}
                  idMessage={idMessage}
                  idCampaign={idCampaign}
                />
              )}
            {checkup &&
              checkup.blocking === 0 &&
              checkup.improvements > 0 &&
              blockToDisplay &&
              blockToDisplay.some(
                e => e.section === 'INFO' && e.toDisplay === true
              ) && (
                <>
                  <ImprovementsCheck />
                  <LinkCheck idMessage={idMessage} />
                </>
              )}
            {checkup && checkup.blocking === 0 && (
              <>
                <Flex
                  alignItems='baseline'
                  justifyContent='flex-start'
                  columnGap='8px'
                  margin='24px 0 0 0'
                >
                  <Checkbox
                    checked={silencedCheck}
                    onChange={handleCheckChange}
                  />
                  <StyledText
                    variant={TEXT_VARIANT_LIGHT}
                    htmlElement='span'
                    onClick={handleClick}
                  >
                    {intl.formatMessage(
                      {
                        id: 'Load.EmailCheckUp.ModalCheckUpTitleDontShowIntl'
                      },
                      {
                        link: content => (
                          <Link
                            variant={LINK_VARIANT_HIGHLIGHT}
                            key={content}
                            onClick={handleClickDontShow}
                          >
                            {content}
                          </Link>
                        )
                      }
                    )}
                  </StyledText>
                </Flex>
              </>
            )}
          </Container>
        </Flex>
      </ModalBody>

      <ModalFoot>
        {checkup && checkup.blocking > 0 && (
          <Button
            onClick={handleOverview}
            kind={BUTTON_VARIANT_SECONDARY}
            data-mu-test='overview-message__modal-checkup-button-cancel'
          >
            {intl.formatMessage({ id: 'Load.NavMenu.Overview' })}
          </Button>
        )}

        {checkup && checkup.blocking === 0 && (
          <Button
            onClick={handleIgnoreAndClose}
            kind={BUTTON_VARIANT_SECONDARY}
            data-mu-test='overview-message__modal-checkup-button-cancel'
          >
            {intl.formatMessage({ id: 'Load.MailupCommon.Ignore' })}
          </Button>
        )}

        <Button
          onClick={handleSaveAndClose}
          kind={BUTTON_VARIANT_PRIMARY}
          data-mu-test='overview-message__modal-checkup-button-save'
        >
          {intl.formatMessage({ id: 'Load.EmailCheckUp.GoToCheckUp' })}
        </Button>
      </ModalFoot>
    </Modal>
  )
}

ModalCheckup.propTypes = {
  idMessage: PropTypes.number,
  messageType: PropTypes.any,
  isBee: PropTypes.bool,
  idCampaign: PropTypes.number,
  startWhen: PropTypes.bool
}

export default ModalCheckup

const Container = styled.div`
  > *:not(:first-child):not(:last-child) {
    border-top: 1px solid var(--geyser);
    margin-top: 24px;
    padding-top: 24px;
  }

  .dynamic-field-item--container + .check-item--container {
    border-top: 1px solid var(--geyser);
    margin-top: 24px;
    padding-top: 24px;
  }
  .check-item--container + .check-item--container {
    border-top: 1px solid var(--geyser);
    margin-top: 16px;
    padding-top: 16px;
  }
`
const StyledText = styled(Text)`
  :hover {
    cursor: pointer;
  }
`
